import * as React from 'react';
import {
  Card,
  CardBody,
  ExpandableSection,
  FormGroup,
  FormSelect,
  FormSelectOption,
  Grid,
  GridItem,
  Title,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  deliveredIn,
  deliveredPrevious,
  deliveryMonths,
  discardedIn,
  stateCounts,
} from '../../../../shared/riskregister/delivery';
import { unitValue } from '../../../../shared/riskregister/grouping';
import {
  CHG_PRACTICE_WINDOWS,
  isChgPracticeWindow,
  type ChgPracticeWindow,
  type DeliveryState,
  type GroupDimension,
  type Payload,
  type Row,
  type ViewState,
} from '../../../../shared/riskregister/types';

const DELIVERY_COLORS: Record<DeliveryState, string> = {
  deployed: 'var(--accent-green)',
  pending: 'var(--accent-amber)',
  nochg: 'var(--status-neutral-border)',
  gap: 'var(--accent-red)',
  discarded: 'var(--text-muted)',
};

const CREDIT_OPTIONS: Array<{ value: GroupDimension; label: string }> = [
  { value: 'project', label: 'Jira project' },
  { value: 'assignee', label: 'Owner' },
  { value: 'svp', label: 'Platform area' },
  { value: 'epic', label: 'Initiative' },
];

const DeliveryTile: React.FunctionComponent<{
  title: string;
  value: number;
  subtitle: string;
  tone: 'success' | 'info' | 'neutral';
}> = ({ title, value, subtitle, tone }) => (
  <Card isCompact className={`risk-register__metric-card risk-register__metric-card--${tone}`}>
    <CardBody>
      <Title headingLevel="h3" size="md">
        {title}
      </Title>
      <strong aria-label={`${title}: ${value}`}>{value}</strong>
      <p>{subtitle}</p>
    </CardBody>
  </Card>
);

export const DeliveredPanel: React.FunctionComponent<{
  payload: Payload;
  rows: Row[];
  state: ViewState;
  onChange: (change: Partial<ViewState>) => void;
  chgWindow: ChgPracticeWindow;
  onChgWindowChange: (window: ChgPracticeWindow) => void;
  isDeliveredItemsExpanded: boolean;
  onDeliveredItemsExpandedChange: (isExpanded: boolean) => void;
}> = ({
  payload,
  rows,
  state,
  onChange,
  chgWindow,
  onChgWindowChange,
  isDeliveredItemsExpanded,
  onDeliveredItemsExpandedChange,
}) => {
  const window = Number(state.dwin);
  const delivered = React.useMemo(() => deliveredIn(rows, window, payload.as_of), [payload.as_of, rows, window]);
  const previous = React.useMemo(() => deliveredPrevious(rows, window, payload.as_of), [payload.as_of, rows, window]);
  const discarded = React.useMemo(() => discardedIn(rows, window, payload.as_of), [payload.as_of, rows, window]);
  const counts = React.useMemo(() => stateCounts(delivered, payload.deliv_order), [delivered, payload.deliv_order]);
  const months = React.useMemo(() => deliveryMonths(rows, payload), [payload, rows]);
  const chartData = React.useMemo(
    () =>
      months.map((month) => ({
        month: month.month,
        ...month.counts,
      })),
    [months],
  );
  const deployed = counts.deployed;
  const coverage = delivered.length ? Math.round((deployed / delivered.length) * 100) : 0;
  const ranking = React.useMemo(() => {
    const entries = new Map<string, Row[]>();
    delivered.forEach((row) => {
      const unit = unitValue(row, state.deliverby, payload);
      entries.set(unit, [...(entries.get(unit) || []), row]);
    });
    return Array.from(entries, ([unit, unitRows]) => ({ unit, rows: unitRows })).sort(
      (left, right) => right.rows.length - left.rows.length || left.unit.localeCompare(right.unit),
    );
  }, [delivered, payload, state.deliverby]);

  return (
    <>
      <Grid hasGutter>
        <GridItem span={12} md={6}>
          <FormGroup label="Delivered within" fieldId="risk-register-delivery-window">
            <FormSelect
              id="risk-register-delivery-window"
              value={state.dwin}
              onChange={(_event, dwin) => onChange({ dwin })}
            >
              {payload.deliv_windows.map((option) => (
                <FormSelectOption key={option} value={String(option)} label={`${option} days`} />
              ))}
            </FormSelect>
          </FormGroup>
        </GridItem>
        <GridItem span={12} md={6}>
          <FormGroup label="Credit delivery to" fieldId="risk-register-delivery-credit">
            <FormSelect
              id="risk-register-delivery-credit"
              value={state.deliverby}
              onChange={(_event, deliverby) => onChange({ deliverby: deliverby as GroupDimension })}
            >
              {CREDIT_OPTIONS.map((option) => (
                <FormSelectOption key={option.value} value={option.value} label={option.label} />
              ))}
            </FormSelect>
          </FormGroup>
        </GridItem>
        <GridItem span={12} md={6}>
          <FormGroup label="CHG practice aggregate period" fieldId="risk-register-chg-practice-window">
            <FormSelect
              id="risk-register-chg-practice-window"
              value={String(chgWindow)}
              aria-describedby="risk-register-chg-practice-window-description"
              onChange={(_event, value) => {
                const next = Number(value);
                if (isChgPracticeWindow(next)) onChgWindowChange(next);
              }}
            >
              {CHG_PRACTICE_WINDOWS.map((option) => (
                <FormSelectOption key={option} value={String(option)} label={`${option} days`} />
              ))}
            </FormSelect>
            <p id="risk-register-chg-practice-window-description">
              Controls only the per-project CHG practice aggregate; it does not limit linked CHG evidence or delivery
              windows.
            </p>
          </FormGroup>
        </GridItem>
      </Grid>

      <Grid hasGutter>
        <GridItem span={12} sm={6} lg={3}>
          <DeliveryTile
            title="Paydown"
            value={delivered.length}
            subtitle={`Resolved in the last ${window} days.`}
            tone="success"
          />
        </GridItem>
        <GridItem span={12} sm={6} lg={3}>
          <DeliveryTile
            title="Deployed with CHG evidence"
            value={deployed}
            subtitle={`${coverage}% CHG coverage among paydown.`}
            tone="success"
          />
        </GridItem>
        <GridItem span={12} sm={6} lg={3}>
          <DeliveryTile
            title="Prior-window paydown"
            value={previous.length}
            subtitle={`Resolved ${window + 1} to ${window * 2} days ago.`}
            tone="info"
          />
        </GridItem>
        <GridItem span={12} sm={6} lg={3}>
          <DeliveryTile
            title="Discarded"
            value={discarded.length}
            subtitle="Shown separately and never counted as paydown."
            tone="neutral"
          />
        </GridItem>
      </Grid>

      <p>
        CHG coverage: {deployed} of {delivered.length} paydown item{delivered.length === 1 ? '' : 's'} have a linked,
        closed change record ({coverage}%).
      </p>

      {chartData.length ? (
        <div
          className="risk-register__chart"
          role="img"
          aria-label="Monthly delivery outcomes, including discarded items"
        >
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={chartData} margin={{ top: 16, right: 24, left: 0, bottom: 4 }}>
              <CartesianGrid stroke="var(--border)" vertical={false} />
              <XAxis
                dataKey="month"
                tick={{ fill: 'var(--text-secondary)' }}
                tickLine={false}
                axisLine={{ stroke: 'var(--border)' }}
                minTickGap={28}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fill: 'var(--text-secondary)' }}
                tickLine={false}
                axisLine={{ stroke: 'var(--border)' }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--bg-secondary)',
                  border: '1px solid var(--border)',
                  color: 'var(--text-primary)',
                }}
              />
              <Legend />
              {payload.deliv_order.map((deliveryState) => (
                <Bar
                  key={deliveryState}
                  dataKey={deliveryState}
                  stackId="delivery"
                  name={payload.deliv_labels[deliveryState]}
                  fill={DELIVERY_COLORS[deliveryState]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p className="risk-register__empty">No resolved delivery outcomes are available for this selection.</p>
      )}

      <Title headingLevel="h3" size="md">
        Who delivered it
      </Title>
      {ranking.length ? (
        <div className="risk-register__table-wrap">
          <Table aria-label="Delivery credited by selected dimension" variant="compact">
            <Thead>
              <Tr>
                <Th scope="col">Credited to</Th>
                <Th scope="col">Paydown</Th>
                <Th scope="col">CHG evidence</Th>
              </Tr>
            </Thead>
            <Tbody>
              {ranking.map((entry) => (
                <Tr key={entry.unit}>
                  <Td dataLabel="Credited to">{entry.unit}</Td>
                  <Td dataLabel="Paydown">{entry.rows.length}</Td>
                  <Td dataLabel="CHG evidence">{entry.rows.filter((row) => row.dstate === 'deployed').length}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </div>
      ) : (
        <p className="risk-register__empty">No paydown is credited in this window.</p>
      )}

      <ExpandableSection
        toggleText={`Delivered items (${delivered.length})`}
        isExpanded={isDeliveredItemsExpanded}
        onToggle={(_event, isExpanded) => onDeliveredItemsExpandedChange(isExpanded)}
      >
        {delivered.length ? (
          <div className="risk-register__table-wrap">
            <Table aria-label="Delivered Risk Register items" variant="compact">
              <Thead>
                <Tr>
                  <Th scope="col">Ticket</Th>
                  <Th scope="col">Summary</Th>
                  <Th scope="col">Resolved</Th>
                  <Th scope="col">Outcome</Th>
                  <Th scope="col">Change records</Th>
                </Tr>
              </Thead>
              <Tbody>
                {delivered.map((row) => (
                  <Tr key={row.key}>
                    <Td dataLabel="Ticket">
                      <a href={`${payload.jira}${row.key}`} target="_blank" rel="noreferrer">
                        {row.key}
                      </a>
                    </Td>
                    <Td dataLabel="Summary">{row.summary}</Td>
                    <Td dataLabel="Resolved">{row.resolved}</Td>
                    <Td dataLabel="Outcome">{row.dstate ? payload.deliv_labels[row.dstate] : '—'}</Td>
                    <Td dataLabel="Change records">
                      {row.chgs.length
                        ? row.chgs.map((change, index) => (
                            <React.Fragment key={change}>
                              {index ? ', ' : ''}
                              <a href={`${payload.jira}${change}`} target="_blank" rel="noreferrer">
                                {change}
                              </a>
                            </React.Fragment>
                          ))
                        : 'No linked change record'}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </div>
        ) : (
          <p className="risk-register__empty">No paydown occurred in this window.</p>
        )}
      </ExpandableSection>

      <ul>
        <li>Every outcome is dated from Jira resolution, including discarded work.</li>
        <li>Discarded work is shown in the chart and tile but is never counted as paydown.</li>
        <li>Deployed means a linked CHG record is closed; resolution alone is not deployment evidence.</li>
        <li>
          Gap means a change-managed project has no linked change record; no-change-record means its project lacks that
          practice.
        </li>
        <li>
          {payload.chg_unreadable.length} linked change record{payload.chg_unreadable.length === 1 ? ' was' : 's were'}{' '}
          unreadable in this load.
        </li>
      </ul>
    </>
  );
};
